import { ClientReviewVersions } from './ClientReviewVersions';
import '@overlayqa/shared/ui/styles.css';
import { Share01Icon } from '@hugeicons/core-free-icons';
import { CopyButton, ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { timeAgo } from '@/lib/utils';
import { useRef, useState } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import { Delete02Icon, ViewIcon, PencilEdit02Icon, Mail01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { clientReviewDoneLabel } from '@overlayqa/shared';
import { ClientEmailsModal } from './ClientEmailsModal';
import { useClientReviewInvitesEnabled } from '../../hooks/useClientReviewInvites';
import { capture } from '@/lib/analytics';
import { useClientReviews, useUpdateClientReview, useDeleteClientReview, useRecaptureClientReview } from '../../hooks/useClientReviews';
import { useProject } from '../../hooks/useProjects';
import { resolveClientReviewInitialUrl } from '../../lib/clientReviewInitialUrl';
import { ClientReviewCreateModal } from './ClientReviewCreateModal';
import { ClientReviewResponsiveSetting } from './ClientReviewResponsiveSetting';
import { DeletionConfirmDialog } from '@/components/shared/DeletionConfirmDialog';
import { LoadError } from '../shared/IssuesLoadError';
import { reviewShareUrl } from '../../lib/reviewShareUrl';
import { copyText } from '../../lib/copyText';
import type { ClientReview, ClientReviewRecipient } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { ModalPortal } from '@/components/shared/ModalPortal';

log.info('[ClientReviewSection] Module loaded');

interface ClientReviewSectionProps {
  projectId: string;
  onClose: () => void;
}

type ReviewWithCount = ClientReview & { commentCount: number };

function computeStatus(review: ClientReview): 'Active' | 'Expired' | 'Revoked' {
  log.info('[ClientReviewSection] computeStatus', { id: review.id });
  if (review.revokedAt) return 'Revoked';
  if (review.expiresAt && new Date(review.expiresAt) < new Date()) return 'Expired';
  return 'Active';
}

/** Preserve the dashboard's legacy selection fallback through the shared control. */
async function writeClientLink(text: string) {
  if (!await copyText(text)) throw new Error('Clipboard access is blocked');
}

const ICON_BUTTON = 'inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors shrink-0';
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** Where one client's reminders stand. */
function recipientState(c: ClientReviewRecipient, currentVersionId?: string | null): string {
  const olderInvite = c.invitedVersionId && currentVersionId && c.invitedVersionId !== currentVersionId;
  const versionLabel = c.invitedVersionSequence ? `V${c.invitedVersionSequence}` : 'an earlier version';
  if (olderInvite && c.done) return `Done on ${versionLabel}`;
  if (olderInvite && c.commented) return `Commented on ${versionLabel}`;
  if (olderInvite && c.invitedAt && !c.unsubscribed) return `Invited to ${versionLabel}`;
  if (c.done) return 'Done reviewing';
  if (c.unsubscribed) return 'Unsubscribed';
  if (c.commented) return 'Commented';
  if (c.lastReminderAt) return `Reminded ${shortDate(c.lastReminderAt)}`;
  if (c.invitedAt) return `Invited ${shortDate(c.invitedAt)}`;
  if (c.inviteFailed) return "Email didn't send";
  if (c.inviteUnconfirmed) return 'Delivery unconfirmed';
  if (c.invitePending) return 'Email queued';
  return 'Not sent';
}

/** With several clients, count who is finished so one done client never hides one who has not responded. */
function remindersLabel(clients: ClientReviewRecipient[], currentVersionId?: string | null): string {
  // A failed invite wins over everything: the owner must see that someone was never emailed.
  const failed = clients.filter(c => c.inviteFailed).length;
  if (failed) return failed === 1 ? "1 email didn't send" : `${failed} emails didn't send`;
  if (clients.some(c => c.inviteUnconfirmed)) return 'Delivery unconfirmed';
  if (clients.some(c => c.invitePending)) return 'Email queued';
  // Invites switched off: nothing went out, and the owner must be able to see that.
  if (clients.some(c => !c.invitedAt && !c.inviteFailed)) return 'Not sent';
  if (clients.length === 1) return recipientState(clients[0], currentVersionId);
  const finished = clients.filter(c => c.done || c.commented).length;
  const older = clients.some(c => c.invitedVersionId && currentVersionId && c.invitedVersionId !== currentVersionId);
  return `${finished} of ${clients.length} responded${older ? ' to their invited versions' : ''}`;
}

function StatusBadge({ status }: { status: 'Active' | 'Expired' | 'Revoked' }) {
  const styles = {
    Active: 'bg-green-50 text-green-700 border-green-200',
    Expired: 'bg-gray-50 text-gray-500 border-gray-200',
    Revoked: 'bg-red-50 text-red-600 border-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-[var(--oq-corner-compact)] oq-text-micro border ${styles[status]}`}>
      {status}
    </span>
  );
}

function ReviewRow({ review, onRevoke, onDelete, onRecapture, onEditClients, showClients, isRevoking, isDeleting, isRecapturing }: {
  review: ReviewWithCount;
  /** False while invites are switched off: the Client and Reminders cells are not rendered. */
  showClients: boolean;
  onEditClients: (review: ReviewWithCount) => void;
  onRevoke: (id: string, revoke: boolean) => void;
  onDelete: (id: string) => void;
  onRecapture: (id: string) => void;
  isRevoking: boolean;
  isDeleting: boolean;
  isRecapturing: boolean;
}) {
  const status = computeStatus(review);
  // A snapshot stores the page's html but keeps its stylesheets as links to the
  // live site, so that site's next deploy 404s those build-hashed assets and the
  // stored page renders unstyled. Re-capture refreshes it without minting a new
  // link, which is what keeps the comment thread and any URL already shared.
  //
  // Offered only for an ACTIVE SNAPSHOT: a live review holds no stored snapshot
  // to refresh, and re-capturing a revoked or expired link would quietly revive
  // a page whose owner already withdrew it. The server refuses all three too.
  const canRecapture = review.mode === 'snapshot' && status === 'Active';
  const shareUrl = reviewShareUrl(review.token);
  const truncatedUrl = shareUrl.length > 32 ? `${shareUrl.slice(0, 32)}…` : shareUrl;
  const clients = review.clients ?? [];

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`client-review-row-${review.id}`}>
      {/* `max-w-0 w-full` is what lets this column SHRINK. In an auto-layout
          table a cell is sized by its content's min-content width, so the 32-char
          mono URL held the whole table wider than the panel: measured on the mini
          2026-09-04 at 1022px of table inside a 918px card, which put Revoke
          (1176-1235) and Delete (1243-1267) past the card's right edge at ~1179.
          They were reachable only by scrolling `div.overflow-auto`, which nobody
          does to find Delete. max-w-0 makes the browser treat this column's
          preferred width as 0 and w-full makes it absorb what is left, so the
          `truncate` below finally does its job and the table fits. The full URL
          was already in the cell's `title`, so nothing is lost by truncating
          harder. Gated by verify-client-review-preview-as-client.ts, watched
          failing at +104px on 7443255a. */}
      <td className="px-4 py-3 max-w-0 w-full min-w-[200px]">
        <div className="flex items-center gap-2 min-w-0">
          {/* A link title, when set, names the row; the URL stays in the tooltip and behind Copy. */}
          <span
            className={`truncate ${review.title ? 'oq-text-body text-gray-800' : 'oq-text-code text-gray-600'}`}
            title={shareUrl}
            data-testid="client-review-url"
          >
            {review.title || truncatedUrl}
          </span>
          <CopyButton label="Copy link" copyText={() => shareUrl} writeClipboard={writeClientLink} />
          {status === 'Active' && <ClientReviewVersions review={review} />}
          {status === 'Active' && (
            // Beside Copy link, not in ACTIONS, for two reasons that agree.
            //
            // SEMANTIC: Copy and Preview are both about the LINK; Re-capture,
            // Revoke and Delete change its STATE. Grouping them that way is what
            // a reader expects, and it puts the preview one pixel from the URL
            // it previews.
            //
            // MEASURED: in ACTIONS it made the snapshot row four controls wide,
            // and the panel card is `max-w-[918px] overflow-hidden`, so the row
            // ran past the card and REVOKE AND DELETE BECAME UNREACHABLE.
            // Measured on the mini 2026-09-04, per control: Preview 973-1083,
            // Re-capture 1091-1172, Revoke 1180-1239, Delete 1247-1271, against a
            // card whose right edge is ~1179. Two and three controls fit; four did
            // not. Caught by reading a screenshot on a step that had just reported
            // PASS -- the aggregate probe was measuring the viewport overlay
            // rather than the card, so it saw 169px of slack that did not exist.
            //
            // 2026-10-12 (client feedback reminders): now an eye ICON button
            // beside Copy, in a 1180px card, so the Client and Reminders columns
            // fit; the 918px measurements above describe the old text button.
            //
            // Offered on an Active link only: the server refuses a revoked or
            // expired token, so the preview would land on the not-found page.
            <a
              href={`${shareUrl}?preview=client`}
              target="_blank"
              rel="noopener noreferrer"
              className={ICON_BUTTON}
              aria-label="Preview as client"
              title="Preview as client: open this link the way your client sees it"
              onClick={() => capture('client_review_preview_clicked', { surface: 'client_reviews_list' })}
            >
              <HugeiconsIcon icon={ViewIcon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          )}
        </div>
        {/* Responsive moved to its own column (client feedback reminders); main's Done reviewing label stays under the URL. */}
        {clientReviewDoneLabel(review.doneReviewers) && (
          <p className="mt-1 flex items-center gap-1 oq-text-caption text-gray-600">
            <HugeiconsIcon icon={Tick02Icon} size={14} strokeWidth={2} color="currentColor" className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />
            {clientReviewDoneLabel(review.doneReviewers)}
          </p>
        )}
      </td>
      <td className="px-2 py-3 oq-text-caption text-gray-600 whitespace-nowrap">
        {review.mode === 'live' ? 'Live' : 'Snapshot'}
      </td>
      <td className="px-2 py-3">
        <ClientReviewResponsiveSetting id={review.id} allowed={review.allowResponsiveViews !== false} />
      </td>
      <td className="px-2 py-3 oq-text-caption text-gray-600 text-right tabular-nums">
        {review.commentCount}
      </td>
      <td className="px-2 py-3 oq-text-caption text-gray-600 text-right tabular-nums">
        {review.visitCount}
      </td>
      <td className="px-2 py-3 oq-text-caption text-gray-600 whitespace-nowrap">
        {review.lastVisitedAt ? timeAgo(review.lastVisitedAt, { justNow: 'Just now' }) : 'Never'}
      </td>
      {showClients && (<>
      <td className="px-2 py-3 oq-text-caption text-gray-600 whitespace-nowrap">
        {clients.length === 0 ? (
          <span className="text-gray-600">No client</span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <span className="truncate max-w-[120px]" title={clients.map(c => c.email).join(', ')}>{clients[0].email}</span>
            {clients.length > 1 && <span className="text-gray-400">+{clients.length - 1}</span>}
            <button
              type="button"
              className={ICON_BUTTON}
              aria-label="Edit client emails"
              title="Edit client emails"
              onClick={() => { capture('client_emails_edit_clicked', { client_count: clients.length }); onEditClients(review); }}
            >
              <HugeiconsIcon icon={PencilEdit02Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </span>
        )}
      </td>
      <td className="px-2 py-3 oq-text-caption text-gray-600 whitespace-nowrap">
        {clients.length === 0 ? (
          <span className="inline-flex items-center gap-2">
            <span className="text-gray-600">Not set</span>
            <button
              type="button"
              className="btn-secondary btn-sm whitespace-nowrap"
              onClick={() => { capture('client_reminders_setup_clicked', {}); onEditClients(review); }}
            >
              <HugeiconsIcon icon={Mail01Icon} size={14} color="currentColor" className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
              <span className="text-cap-trim">Set up</span>
            </button>
          </span>
        ) : (
          remindersLabel(clients, review.currentVersionId)
        )}
      </td>
      </>)}
      <td className="px-2 py-3">
        <StatusBadge status={status} />
      </td>
      <td className="px-2 py-3">
        <div className="flex items-center gap-2 justify-end">
          {canRecapture && (
            <button
              type="button"
              className="btn-ghost btn-sm whitespace-nowrap"
              onClick={() => onRecapture(review.id)}
              disabled={isRecapturing}
              title="Capture this page as a new saved version. Earlier feedback stays with its original page."
            ><ModalAction kind="retry">
              {isRecapturing ? 'Re-capturing...' : 'Re-capture'}
            </ModalAction></button>
          )}
          {status !== 'Expired' && (
            <button
              type="button"
              className="btn-ghost btn-sm whitespace-nowrap"
              onClick={() => onRevoke(review.id, !review.revokedAt)}
              disabled={isRevoking}
            ><ModalAction kind={review.revokedAt ? "retry" : "unlink"}>
              {review.revokedAt ? 'Restore' : 'Revoke'}
            </ModalAction></button>
          )}
          <button
            type="button"
            aria-label="Delete review link"
            className="inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            onClick={() => onDelete(review.id)}
            disabled={isDeleting}
          >
            <HugeiconsIcon icon={Delete02Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export function ClientReviewSection({ projectId, onClose }: ClientReviewSectionProps) {
  log.info('[ClientReviewSection] Render:', { projectId });

  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, onClose);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [recapturingId, setRecapturingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingClients, setEditingClients] = useState<ReviewWithCount | null>(null);

  const reviewsQuery = useClientReviews(projectId);
  // Client emails are hidden while invites are switched off (Emily, 2026-10-12).
  const invitesOn = useClientReviewInvitesEnabled();
  // Only the primaryDomain is read here (the create modal's prefill fallback).
  // Same query key IssuesPage already holds, so on that page this is a cache
  // hit; on ProjectsPage it is one GET per open.
  const projectQuery = useProject(projectId);
  const updateReview = useUpdateClientReview();
  const deleteReview = useDeleteClientReview();
  const recaptureReview = useRecaptureClientReview();

  const reviews = reviewsQuery.data ?? [];
  // The empty state carries its own centred "Share client link" CTA; every other
  // body state (loading, error, links present) gets the toolbar button above
  // the list instead, so exactly ONE create control is on screen at a time.
  const isEmptyState = !reviewsQuery.isLoading && !reviewsQuery.error && reviews.length === 0;
  // Recomputed on every render on purpose: the modal adopts a value that
  // lands after it opened (both queries can still be loading at the click).
  const createInitialUrl = resolveClientReviewInitialUrl({
    reviews: reviewsQuery.data,
    project: projectQuery.data,
  });

  const openCreateModal = () => {
    log.info('[ClientReviewSection] Opening create modal', {
      projectId,
      initialUrl: createInitialUrl,
      reviewsLoaded: reviewsQuery.data !== undefined,
      projectLoaded: projectQuery.data !== undefined,
    });
    setShowCreateModal(true);
  };

  const handleRecapture = (id: string) => {
    log.info('[ClientReviewSection] Re-capturing review:', { id });
    setRecapturingId(id);
    recaptureReview.mutate(id, {
      onSuccess: () =>
        toast.success('Re-capturing the page. The link stays the same, and its comments are kept.'),
      onError: (err) =>
        toast.error(err instanceof Error && err.message ? err.message : 'Could not re-capture this page. Please try again.'),
      onSettled: () => setRecapturingId(null),
    });
  };

  const handleRevoke = (id: string, revoke: boolean) => {
    log.info('[ClientReviewSection] Revoking review:', { id, revoke });
    updateReview.mutate(
      { id, revoke },
      {
        onError: (err) =>
          toast.error(err instanceof Error && err.message ? err.message : `Failed to ${revoke ? 'revoke' : 'restore'} link. Please try again.`),
      },
    );
  };

  const handleDelete = (id: string) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    if (!confirmDeleteId) return;
    const id = confirmDeleteId;
    log.info('[ClientReviewSection] Deleting review:', { id });
    setDeletingId(id);
    deleteReview.mutate(id, {
      onSettled: () => {
        setDeletingId(null);
        setConfirmDeleteId(null);
      },
      onError: (err) =>
        toast.error(err instanceof Error && err.message ? err.message : 'Failed to delete link. Please try again.'),
    });
  };

  const overlay = (
    <>
      <div
        ref={dialogRef}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-review-section-title"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <section className="bg-white rounded-xl w-full max-w-[1180px] max-h-[80vh] flex flex-col shadow-xl overflow-hidden">
          {/* Header. The shared modal header bar, NOT `.section-label`: that is
              the card-header class (`@apply uppercase tracking-wider`,
              styles/index.css:753), and `text-transform` INHERITS into the
              title below, which rendered "CLIENT REVIEWS".

              CORRECTED 2026-08-23 by measuring it: the uppercase never reached
              a <button> (the UA stylesheet sets `text-transform: none` on form
              controls); the defect was always the TITLE. The header's
              "Create link" button that measurement used moved below the header
              on 2026-09-03 (Emily); the harness now reads the close button.

              The `#dashboard`/`#issues`/`#issue-detail` override at
              index.css:765-767 does not rescue it either: this modal renders
              through <ModalPortal>, so the overlay is a direct child of
              document.body and those descendant selectors cannot reach it.
              Both facts are pinned live by
              packages/dashboard/e2e/verify-modal-header-case.ts (A/B, control
              arm reproduces). `overflow-hidden` on the panel above is
              load-bearing too, since the body is a full-bleed table. */}
          <header className="px-6 py-2 border-b border-gray-100 flex items-center justify-between shrink-0">
            <ModalTitle icon={Share01Icon} id="client-review-section-title" className="oq-dialog__title">Client links</ModalTitle>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          {/* "Share client link" lives above the list, not in the header (Emily,
              2026-09-03: "remove the create link from the client review modal
              header"). It is the ONLY create path once a project has links
              (the empty state has its own centred CTA, so it is hidden there),
              and it stays offered while the list loads or fails, exactly as
              the header button it replaces was: verify-client-review-url-prefill
              clicks it during "Loading...". A shrink-0 sibling of the scroll
              region, so a long list cannot scroll it away. */}
          {!isEmptyState && (
            <div className="px-4 py-3 border-b border-gray-100 flex justify-end shrink-0">
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={openCreateModal}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" aria-hidden="true"><path d="M12 4v16m8-8H4" /></svg>
                <span className="text-cap-trim">Share client link</span>
              </button>
            </div>
          )}

          {/* Body */}
          <div className="overflow-auto flex-1">
            {reviewsQuery.isLoading ? (
              <div className="p-6 state-note text-gray-500 text-center">Loading...</div>
            ) : reviewsQuery.error ? (
              <div className="p-6">
                <LoadError
                  compact
                  resource="review links"
                  error={reviewsQuery.error as Error}
                  onRetry={() => reviewsQuery.refetch()}
                />
              </div>
            ) : reviews.length === 0 ? (
              <div className="p-8 text-center">
                <p className="state-note text-gray-500">No client links yet.</p>
                <p className="oq-text-caption text-gray-600 mt-1">Create one to let stakeholders leave feedback.</p>
                <button
                  type="button"
                  className="btn-primary gap-2 mt-4"
                  onClick={openCreateModal}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" aria-hidden="true"><path d="M12 4v16m8-8H4" /></svg>
                  <span className="text-cap-trim">Share client link</span>
                </button>
              </div>
            ) : (
              <table className="w-full text-left" aria-label="Client links">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 oq-text-label text-gray-500">URL</th>
                    <th className="px-2 py-3 oq-text-label text-gray-500">Mode</th>
                    <th className="px-2 py-3 oq-text-label text-gray-500">Responsive</th>
                    <th className="px-2 py-3 oq-text-label text-gray-500 text-right">Comments</th>
                    <th className="px-2 py-3 oq-text-label text-gray-500 text-right">Visits</th>
                    <th className="px-2 py-3 oq-text-label text-gray-500">Last seen</th>
                    {invitesOn && <th className="px-2 py-3 oq-text-label text-gray-500">Client</th>}
                    {invitesOn && <th className="px-2 py-3 oq-text-label text-gray-500">Reminders</th>}
                    <th className="px-2 py-3 oq-text-label text-gray-500">Status</th>
                    <th className="px-2 py-3 oq-text-label text-gray-500 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review) => (
                    <ReviewRow
                      key={review.id}
                      review={review}
                      onRevoke={handleRevoke}
                      onRecapture={handleRecapture}
                      onEditClients={setEditingClients}
                      showClients={invitesOn}
                      isRecapturing={recapturingId === review.id}
                      onDelete={handleDelete}
                      isRevoking={updateReview.isPending}
                      isDeleting={deleteReview.isPending && deletingId === review.id}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>

      {showCreateModal && (
        <ClientReviewCreateModal
          projectId={projectId}
          initialUrl={createInitialUrl}
          guestDefaults={projectQuery.data ? {
            welcomeEnabled: projectQuery.data.clientReviewWelcomeEnabled === true,
            welcomeNote: projectQuery.data.clientReviewWelcomeNote ?? '',
            privateComments: projectQuery.data.clientReviewPrivateComments === true,
            allowResponsiveViews: projectQuery.data.clientReviewAllowResponsive !== false,
          } : undefined}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {invitesOn && editingClients && (
        <ClientEmailsModal
          reviewId={editingClients.id}
          initialEmails={(editingClients.clients ?? []).map(c => c.email)}
          failedEmails={(editingClients.clients ?? []).filter(c => c.inviteFailed).map(c => c.email)}
          onClose={() => setEditingClients(null)}
        />
      )}

      <DeletionConfirmDialog
        open={confirmDeleteId !== null}
        request={confirmDeleteId !== null ? { kind: 'clientReview', ids: [confirmDeleteId] } : null}
        fallback={{ title: 'Delete review link', message: 'Delete this review link? Clients with this link will no longer be able to access it.', confirmLabel: 'Delete' }}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
        loading={deleteReview.isPending}
      />
    </>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}

